import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import chatbotAxios from './axios';

// ─── Must match backend ChatbotController::MAX_QUESTIONS exactly ─────────────
const MAX_QUESTIONS = 5;

const CONTACT_INFO = {
  email: "contactus@exdnow.com",
  phone: "+1 (800) 123-4567",
};

const GREETING =
  // "Hi there! 👋 I'm ExD's assistant. I can answer questions about our services, products, and how we work. What would you like to know?";
  "Hi there! 👋 Ready to build something? Just spill it, what's on your mind?";

// const LIMIT_MESSAGE =
//   `You've reached the limit for this session today. For further assistance, please reach out via Contact Us Page or contact support team:\n\n` +
//   `📧 ${CONTACT_INFO.email}\n\n`;

const LIMIT_MESSAGE =
  `<div style="display:flex; flex-direction:column;">
        <p style="font-size:13px">You've reached the limit for this session today.</p>

        <p style="font-size:13px">You can reach out to us through any of the following methods:</p>

        <a href="https://www.exddigital.com/contact-us" target="_blank">1. Contact Us</a>
        <a href="https://calendar.app.google/zrGikproai5G8KVb8" target="_blank">2. Book Free Consultation Call →</a>
        <a href="mailto:contactus@exdnow.com">3. contactus@exdnow.com</a>
    </div>`;

// ─── Detect mobile (used for layout switching) ────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─── Typing indicator ─────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 4,
      padding: "10px 14px", background: "rgb(255 242 219)",
      borderRadius: "18px 18px 18px 4px", width: "fit-content",
      marginBottom: 12, flexShrink: 0,
    }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 7, height: 7, borderRadius: "50%",
          background: "rgb(247 205 128)", display: "inline-block",
          animation: "exd-bounce 1.2s infinite",
          animationDelay: `${i * 0.2}s`,
        }} />
      ))}
    </div>
  );
}

// ─── Single chat message ──────────────────────────────────────────────────────
function Message({ msg }) {
  const isBot = msg.role === "bot";
  return (
    <div style={{
      display: "flex", justifyContent: isBot ? "flex-start" : "flex-end",
      marginBottom: 10, animation: "exd-fadeSlideIn 0.25s ease",
      flexShrink: 0, width: "100%",
    }}>
      {isBot && (
        <div style={{
          width: 28, height: 28, borderRadius: "50%",
          background: "linear-gradient(90deg, #ebb94d 0%, #FF6800 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, marginRight: 8, marginTop: 2,
          fontSize: 12, color: "#fff", fontWeight: 700,
          fontFamily: "'DM Sans', sans-serif",
        }}>E</div>
      )}
      <div 
      className="exd-message-content"
      style={{
        maxWidth: "78%",
        padding: "10px 14px",
        borderRadius: isBot ? "18px 18px 18px 4px" : "18px 18px 4px 18px",
        background: isBot
          ? "rgb(255 241 215)"
          : "linear-gradient(135deg, rgb(123 121 105), rgb(123 121 105))",
        color: isBot ? "#2D2640" : "#fff",
        // FIX #6 (partial): keep message font readable on mobile
        fontSize: 13,
        lineHeight: 1.55,
        fontFamily: "'DM Sans', sans-serif",
        whiteSpace: "pre-line",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        boxShadow: isBot ? "none" : "0 2px 8px rgba(107,92,231,0.25)",
      }}
       dangerouslySetInnerHTML={{ __html: msg.text }}
      >
        {/* {msg.text} */}
        
      </div>
    </div>
  );
}

// ─── Main chatbot widget ──────────────────────────────────────────────────────
export default function ExdChatbot() {
  const [open, setOpen]                   = useState(false);
  const [messages, setMessages]           = useState([{ role: "bot", text: GREETING }]);
  const [input, setInput]                 = useState("");
  const [loading, setLoading]             = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [limitReached, setLimitReached]   = useState(false);
  const [hasNotification, setHasNotification] = useState(true);

  const [showTooltip, setShowTooltip] = useState(false);

  const location = useLocation();
  const tooltipTimerRef = useRef(null);

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const isMobile       = useIsMobile();

  // Focus input when chat opens; clear notification dot
  useEffect(() => {
    if (open) {
      setHasNotification(false);
       setShowTooltip(false);
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open]);

  // Scroll to latest message / typing indicator
  useEffect(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  }, [messages, loading]);


  // Store pages where tooltip was already shown
  const shownTooltipPagesRef = useRef({});

  // Show tooltip 20s after route change
  useEffect(() => {
    console.log('asd');
    // hide existing tooltip
    setShowTooltip(false);

    // clear previous timer
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }

    // don't show if chat already open
    if (open) return;

    // current page path
    const currentPath = location.pathname;

    // if tooltip already shown on this page, don't show again
    if (shownTooltipPagesRef.current[currentPath]) {
      return;
    }

    tooltipTimerRef.current = setTimeout(() => {
      setShowTooltip(true);
          console.log('tooltip');

      // mark this page as already shown
      shownTooltipPagesRef.current[currentPath] = true;

      // auto hide after 8 seconds
      setTimeout(() => {
        setShowTooltip(false);
      }, 9000);

    }, 10000);

    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, [location.pathname, open]);


  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || limitReached) return;

    setMessages((prev) => [...prev, { role: "user", text }]);
    setInput("");
    setLoading(true);

    try {
      const { data } = await chatbotAxios.post(
        "/api/chatbot/ask",
        { question: text },
        {
          headers: {
            // API key kept in env var — never hard-code in source
            "X-Api-Key": process.env.REACT_APP_CHATBOT_API_KEY,
          },
        }
      );

      // Consume all backend response fields
      setMessages((prev) => [...prev, { role: "bot", text: data.answer }]);
      setQuestionCount(data.questions_used);
      // data.questions_remaining mirrors MAX_QUESTIONS - questions_used from backend
      // data.sources available if you want to show matched FAQ topics

      if (data.limit_reached) {
        setTimeout(() => {
          setMessages((prev) => [...prev, { role: "bot", text: LIMIT_MESSAGE }]);
          setLimitReached(true);
        }, 600);
      }
    } catch (error) {
      const status = error.response?.status;
      let errText;

      if (status === 429) {
        errText = LIMIT_MESSAGE;
        setLimitReached(true);
        setQuestionCount(MAX_QUESTIONS);
      } else if (status === 401) {
        errText = "Unauthorized request. Please refresh the page and try again.";
      } else if (status === 422) {
        errText = "Your question is too short or contains invalid characters. Please try again.";
      } else {
        errText = "Something went wrong. Please check your connection and try again.";
      }

      setMessages((prev) => [...prev, { role: "bot", text: errText }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, limitReached]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Progress bar uses the authoritative server count, capped at MAX_QUESTIONS
  const usedPct     = Math.min((questionCount / MAX_QUESTIONS) * 100, 100);
  const remaining   = Math.max(0, MAX_QUESTIONS - questionCount);

  // ─── Responsive window dimensions ─────────────────────────────────────────
  // FIX #1 + #2 + #4: On mobile, window stretches edge-to-edge with safe-area
  // margins; height uses dvh so the virtual keyboard doesn't clip the window.
  const windowStyle = isMobile
    ? {
        // Full-width minus horizontal safe-area insets
        left:   "env(safe-area-inset-left, 0px)",
        right:  "env(safe-area-inset-right, 0px)",
        width:  "auto",
        // Use 90dvh so content resizes when the soft keyboard appears
        height: "min(90dvh, 600px)",
        bottom: "calc(70px + env(safe-area-inset-bottom, 0px))",
        borderRadius: "20px 20px 0 0",
      }
    : {
        width:  360,
        height: 520,
        bottom: "auto",   // positioned by the flex column gap on desktop
        right:  0,
        borderRadius: 20,
      };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');

        @keyframes exd-fadeSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes exd-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30%            { transform: translateY(-5px); }
        }
        @keyframes exd-popIn {
          from { opacity: 0; transform: scale(0.88) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        @keyframes exd-pulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.1); }
        }

        /* ── Input ── */
        .exd-chat-input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          /* FIX #6: 16px prevents iOS auto-zoom on focus */
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: #2D2640;
          resize: none;
          line-height: 1.4;
          height: 50px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgb(255, 241, 215) transparent;
        }
        .exd-chat-input::placeholder { color: #B0A8D8; }

        /* ── Send button ── */
        .exd-send-btn {
          /* FIX #7: 44px touch target minimum */
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(90deg, #8b795f 0%, #3b3623 100%);
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.15s, opacity 0.15s;
          flex-shrink: 0;
          margin-bottom:4px
        }
        .exd-send-btn:disabled           { opacity: 0.45; cursor: not-allowed; transform: none !important; }
        .exd-send-btn:not(:disabled):hover  { transform: scale(1.08); }
        .exd-send-btn:not(:disabled):active { transform: scale(0.95); }

        /* ── FAB ── */
        .exd-fab {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(90deg, #FCB61D 0%, #FF6800 100%);
          border: none;
          cursor: pointer;
          // box-shadow: 0 4px 18px rgba(252, 182, 29, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s, box-shadow 0.2s;
          position: relative;
          // margin: 15px;
          /* FIX #7: explicit 54px already meets 44px minimum ✅ */
        }
        // .exd-fab:hover  { transform: scale(1.02); box-shadow: 0 6px 22px rgba(252, 182, 29, 0.5); }
        // .exd-fab:active { transform: scale(0.96); }

         @media (max-width: 768px) {
           .exd-fab {
             width: 40px;
             height: 40px;
           }
         }

        /* ── Container positioning (CSS media queries — never affected by JS timing) ── */
        .exd-chatbot-container {
          right: 70px;
          bottom: 37px;
        }
        @media (max-width: 768px) {
          .exd-chatbot-container {
            right: 16px;
            bottom: max(28px, env(safe-area-inset-bottom, 16px));
          }
          .exd-chatbot-container.exd-chatbot-home {
            bottom: calc(4% + 10px);
          }
        }

        .exd-notif-dot {
          position: absolute;
          top: -1px; right: -1px;
          width: 14px; height: 14px;
          background: #FF5B5B;
          border-radius: 50%;
          border: 2px solid #fff;
          animation: exd-pulse 2s infinite;
        }

        /* ── Close button — FIX #7: 44px touch target ── */
        .exd-close-btn {
          background: rgba(255,255,255,0.15);
          border: none;
          /* Visual circle stays 28px but tap area is 44px */
          width: 44px;
          height: 44px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          transition: background 0.15s;
          flex-shrink: 0;
          margin: -8px;   /* compensate for extra size so header doesn't grow */
        }
        .exd-close-btn:hover  { background: rgba(255,255,255,0.25); }

        /* ── Scrollbar ── */
        .exd-messages::-webkit-scrollbar       { width: 4px; }
        .exd-messages::-webkit-scrollbar-track { background: transparent; }
        .exd-messages::-webkit-scrollbar-thumb { background: #DDD9F5; border-radius: 10px; }

        /* ── Mobile-only tweaks ── */
        @media (max-width: 479px) {
          /* FIX #8: When soft keyboard is open on iOS, dvh unit auto-shrinks the
             window so it never gets clipped behind the keyboard. */
          .exd-window-mobile {
            position: fixed !important;
          }
        }
          /* Thin scrollbar for the messages area only */
        .exd-messages::-webkit-scrollbar       { width: 4px; }
        .exd-messages::-webkit-scrollbar-track { background: transparent; }
        .exd-messages::-webkit-scrollbar-thumb { background: #DDD9F5; border-radius: 10px; }

        /* ── Input textarea scrollbar ── */
        .exd-chat-input::-webkit-scrollbar       { width: 4px; }
        .exd-chat-input::-webkit-scrollbar-track { background: transparent; }
        .exd-chat-input::-webkit-scrollbar-thumb { background: rgb(255, 241, 215); border-radius: 10px; }

        .exd-message-content a {
          color: #FF6800;
          font-weight: 600;
          text-decoration: none;
        }

        .exd-message-content a:hover {
          text-decoration: underline;
        }
      `}</style>

      {/*
        FIX #3 + FAB position:
        Desktop: bottom 24px, right 24px
        Mobile:  bottom accounts for iOS safe-area-inset-bottom (home indicator)
      */}
      <div
        className={`exd-chatbot-container${location.pathname === "/" ? " exd-chatbot-home" : ""}`}
        style={{
          position: "fixed",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 12,
          overflow: "hidden"
        }}
      >

        {/* ── Chat window ── */}
        {open && (
          <div
            className={isMobile ? "exd-window-mobile" : ""}
            style={{
              // Shared styles
              background: "#FFFFFF",
              boxShadow: "0 12px 48px rgba(107,92,231,0.18), 0 2px 12px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              animation: "exd-popIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)",
              fontFamily: "'DM Sans', sans-serif",
              // Responsive overrides
              ...windowStyle,
            }}
          >

            {/* Header */}
            <div style={{
              background: "linear-gradient(90deg, rgb(51 48 41) 0%, rgb(255 129 0) 100%)",
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexShrink: 0,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                    stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: "#fff", fontWeight: 600, fontSize: 14, lineHeight: 1, overflow:"hidden" }}>
                  ExD Assistant
                </div>
                {/* <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11.5, marginTop: 2 }}>
                  {limitReached
                    ? "Session ended"
                    : `${remaining} question${remaining !== 1 ? "s" : ""} remaining`}
                </div> */}
              </div>
              {/* FIX #7: 44px touch target on close button */}
              <button
                className="exd-close-btn"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6"  y2="18"/>
                  <line x1="6"  y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Progress bar */}
            <div style={{ height: 3, background: "#EDE9FF", flexShrink: 0 }}>
              <div style={{
                height: "100%",
                width: `${usedPct}%`,
                background: "linear-gradient(90deg, #ebb94d 0%, #FF6800 100%)",
                transition: "width 0.4s ease",
              }} />
            </div>

            {/* Messages — FIX #8: minHeight:0 + flex:1 ensures scroll works even
                when virtual keyboard shrinks the window height */}
            <div
              className="exd-messages"
              style={{
                flex: 1,
                minHeight: 0,       // critical — allows flex child to shrink & scroll
                overflowY: "auto",
                overflowX: "hidden",
                padding: "14px 14px 6px",
                display: "flex",
                flexDirection: "column",
                // FIX #8: overscroll-behavior prevents pull-to-refresh interfering
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",   // smooth momentum scroll on iOS
              }}
            >
              {messages.map((msg, i) => (
                <Message key={i} msg={msg} />
              ))}
              {loading && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div style={{
              padding: "10px 12px",
              borderTop: "1px solid #EDE9FF",
              display: "flex",
              alignItems: "flex-end",
              gap: 8,
              background: "#FAFAFE",
              flexShrink: 0,
              // FIX #8: bottom safe area so input isn't hidden behind home indicator
              paddingBottom: isMobile
                ? "max(10px, env(safe-area-inset-bottom, 10px))"
                : "10px",
            }}>
              {limitReached ? (
                <div style={{
                  flex: 1, textAlign: "center",
                  fontSize: 12.5, color: "#9B8EF0",
                  padding: "6px 0",
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  Chat session ended ·{" "}
                  <a href={`mailto:${CONTACT_INFO.email}`}
                    style={{ color: "#6B5CE7", textDecoration: "none", fontWeight: 500 }}>
                    {CONTACT_INFO.email}
                  </a>
                </div>
              ) : (
                <>
                  <textarea
                    ref={inputRef}
                    className="exd-chat-input"
                    rows={1}
                    placeholder="Ask me anything…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    disabled={loading}
                    style={{ paddingTop: 7 }}
                    // FIX #8: prevent iOS from scrolling the page when typing
                    onFocus={() => {
                      if (isMobile) {
                        setTimeout(() => {
                          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
                        }, 300); // wait for keyboard to fully open
                      }
                    }}
                  />
                  {/* FIX #7: 44px touch target on send button */}
                  <button
                    className="exd-send-btn"
                    onClick={sendMessage}
                    disabled={!input.trim() || loading}
                    aria-label="Send message"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* FAB */}
        {/* Tooltip */}
        {showTooltip && !open && (
          <div
            style={{
              // bottom: "78px",
              // right: "93px",
              bottom: isMobile ? "62px" : "78px",
              right: isMobile ? "63px" : "93px",
              background: "rgb(255, 255, 255)",
              color: "rgb(45, 38, 64)",
              padding: "10px 14px",
              borderRadius: "14px",
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
              fontSize: "13px",
              fontWeight: 500,
              maxWidth: "140px",
              lineHeight: 1.4,
              animation: "exd-popIn 0.25s ease",
              fontFamily: "'DM Sans', sans-serif",
              zIndex: 10000,
              position: "fixed",
              borderBottomRightRadius: "0px",
            }}
          >
            Hi 👋 What brings you here today?

            {/* Tooltip arrow */}
            {/* <div
              style={{
                position: "absolute",
                bottom: -6,
                right: 18,
                width: 12,
                height: 12,
                background: "#fff",
                transform: "rotate(45deg)",
              }}
            /> */}
          </div>
        )}
        <button
          className="exd-fab"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close chat" : "Open chat"}
        >
          {hasNotification && !open && <span className="exd-notif-dott" />}
          {open ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6"  y2="18"/>
              <line x1="6"  y1="6" x2="18" y2="18"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>
      </div>
    </>
  );
}